import React, { useEffect, useState, useContext } from 'react';
import { 
    CSpinner, 
    CAlert, 
    CButton, 
    CButtonGroup, 
    CBadge,
    CCard,
    CCardBody 
} from '@coreui/react';
import { AuthUserContext } from '../../session';
import './Suggestions.scss';

/**
 * Enhanced Local Attractions Component
 * 
 * Features:
 * - Real-time geolocation-based attractions
 * - Multiple data sources (OpenStreetMap, Google Places, Events)
 * - Categorized attractions (restaurants, events, landmarks, etc.)
 * - Distance calculations and ratings
 * - User preferences and filtering
 */
const Suggestions = ({ coordinates }) => {
    const { authUser } = useContext(AuthUserContext);
    const [attractions, setAttractions] = useState([]);
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    const [locationName, setLocationName] = useState('');

    const categories = [
        { id: 'all', name: 'All', icon: '🌟' },
        { id: 'restaurants', name: 'Food', icon: '🍽️' },
        { id: 'events', name: 'Events', icon: '🎉' },
        { id: 'attractions', name: 'Places', icon: '🏛️' },
        { id: 'entertainment', name: 'Fun', icon: '🎭' },
        { id: 'shopping', name: 'Shopping', icon: '🛍️' }
    ];

    useEffect(() => {
        if (coordinates) {
            fetchLocalData();
            reverseGeocode();
        }
    }, [coordinates]);

    // Get location name from coordinates
    const reverseGeocode = async () => {
        try {
            const response = await fetch(
                `https://api.opencagedata.com/geocode/v1/json?q=${coordinates.lat}+${coordinates.lng}&key=${import.meta.env.VITE_OPENCAGE_API_KEY || 'demo_key'}`
            );
            const data = await response.json();
            if (data.results && data.results.length > 0) {
                const location = data.results[0];
                setLocationName(location.components.city || location.components.town || location.components.county || 'Your Area');
            }
        } catch (error) {
            console.error('Error getting location name:', error);
            setLocationName('Your Area');
        }
    };

    // Fetch local attractions and events
    const fetchLocalData = async () => {
        setIsLoading(true);
        setError('');
        
        try {
            await Promise.all([
                fetchNearbyPlaces(),
                fetchLocalEvents(),
                fetchOpenStreetMapData()
            ]);
        } catch (error) {
            console.error('Error fetching local data:', error);
            setError('Unable to load local attractions. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch nearby places using multiple sources
    const fetchNearbyPlaces = async () => {
        try {
            // Sample data - replace with actual Google Places API or similar
            const samplePlaces = [
                {
                    id: 'place_1',
                    name: 'Downtown Art Gallery',
                    category: 'attractions',
                    type: 'Art Gallery',
                    rating: 4.5,
                    distance: 0.8,
                    address: '123 Main St',
                    description: 'Contemporary art gallery featuring local artists',
                    image: '/api/placeholder-gallery.jpg',
                    isOpen: true,
                    priceLevel: 2
                },
                {
                    id: 'place_2',
                    name: 'Riverside Café',
                    category: 'restaurants',
                    type: 'Coffee Shop',
                    rating: 4.7,
                    distance: 0.3,
                    address: '456 River Ave',
                    description: 'Cozy café with river views and fresh pastries',
                    image: '/api/placeholder-cafe.jpg',
                    isOpen: true,
                    priceLevel: 2
                },
                {
                    id: 'place_3',
                    name: 'Central Park',
                    category: 'attractions',
                    type: 'Park',
                    rating: 4.3,
                    distance: 1.2,
                    address: '789 Park Blvd',
                    description: 'Beautiful park with walking trails and playground',
                    image: '/api/placeholder-park.jpg',
                    isOpen: true,
                    priceLevel: 0
                },
                {
                    id: 'place_4',
                    name: 'The Local Theater',
                    category: 'entertainment',
                    type: 'Theater',
                    rating: 4.6,
                    distance: 1.5,
                    address: '321 Theater St',
                    description: 'Historic theater showing latest movies and live performances',
                    image: '/api/placeholder-theater.jpg',
                    isOpen: true,
                    priceLevel: 3
                },
                {
                    id: 'place_5',
                    name: 'Artisan Market',
                    category: 'shopping',
                    type: 'Market',
                    rating: 4.4,
                    distance: 0.9,
                    address: '654 Market Square',
                    description: 'Local artisan market with handmade crafts and food',
                    image: '/api/placeholder-market.jpg',
                    isOpen: true,
                    priceLevel: 2
                }
            ];

            setAttractions(prev => [...prev, ...samplePlaces]);
        } catch (error) {
            console.error('Error fetching places:', error);
        }
    };

    // Fetch local events
    const fetchLocalEvents = async () => {
        try {
            // Sample events data - replace with actual events API (Eventbrite, Meetup, etc.)
            const sampleEvents = [
                {
                    id: 'event_1',
                    name: 'Weekend Farmers Market',
                    category: 'events',
                    type: 'Market',
                    date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // 2 days from now
                    time: '9:00 AM - 2:00 PM',
                    distance: 0.5,
                    address: 'City Square',
                    description: 'Fresh produce, local vendors, and live music',
                    image: '/api/placeholder-farmers-market.jpg',
                    price: 'Free',
                    attendees: 150
                },
                {
                    id: 'event_2',
                    name: 'Jazz in the Park',
                    category: 'events',
                    type: 'Concert',
                    date: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), // 5 days from now
                    time: '7:00 PM - 9:00 PM',
                    distance: 1.2,
                    address: 'Central Park Amphitheater',
                    description: 'Free outdoor jazz concert featuring local musicians',
                    image: '/api/placeholder-jazz.jpg',
                    price: 'Free',
                    attendees: 300
                },
                {
                    id: 'event_3',
                    name: 'Food Truck Festival',
                    category: 'events',
                    type: 'Festival',
                    date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week from now
                    time: '11:00 AM - 8:00 PM',
                    distance: 2.1,
                    address: 'Riverside Park',
                    description: 'Over 20 food trucks featuring diverse cuisines',
                    image: '/api/placeholder-food-trucks.jpg',
                    price: '$5 entry',
                    attendees: 500
                }
            ];

            setEvents(sampleEvents);
        } catch (error) {
            console.error('Error fetching events:', error);
        }
    };

    // Fetch OpenStreetMap data for additional places
    const fetchOpenStreetMapData = async () => {
        try {
            const radius = 2000; // 2km radius
            const query = `
                [out:json][timeout:25];
                (
                    node["amenity"~"restaurant|cafe|bar|pub|fast_food"](around:${radius},${coordinates.lat},${coordinates.lng});
                    node["tourism"~"attraction|museum|gallery|viewpoint"](around:${radius},${coordinates.lat},${coordinates.lng});
                    node["leisure"~"park|garden|playground"](around:${radius},${coordinates.lat},${coordinates.lng});
                    node["shop"~"mall|department_store|bookstore"](around:${radius},${coordinates.lat},${coordinates.lng});
                );
                out geom;
            `;

            const response = await fetch(
                `https://overpass-api.de/api/interpreter?data=${encodeURIComponent(query)}`
            );
            
            if (response.ok) {
                const data = await response.json();
                const osmPlaces = data.elements
                    .filter(element => element.tags && element.tags.name)
                    .slice(0, 10) // Limit to 10 results
                    .map(element => ({
                        id: `osm_${element.id}`,
                        name: element.tags.name,
                        category: getCategoryFromOSM(element.tags),
                        type: element.tags.amenity || element.tags.tourism || element.tags.leisure || element.tags.shop || 'Place',
                        distance: calculateDistance(coordinates.lat, coordinates.lng, element.lat, element.lon),
                        address: element.tags['addr:street'] || 'Address not available',
                        description: element.tags.description || `${element.tags.amenity || element.tags.tourism || 'Local place'} in your area`,
                        isOpen: true,
                        source: 'OpenStreetMap'
                    }));

                setAttractions(prev => [...prev, ...osmPlaces]);
            }
        } catch (error) {
            console.error('Error fetching OSM data:', error);
        }
    };

    // Map OSM tags to our categories
    const getCategoryFromOSM = (tags) => {
        if (tags.amenity && ['restaurant', 'cafe', 'bar', 'pub', 'fast_food'].includes(tags.amenity)) {
            return 'restaurants';
        }
        if (tags.tourism || tags.leisure) {
            return 'attractions';
        }
        if (tags.shop) {
            return 'shopping';
        }
        return 'attractions';
    };

    // Calculate distance between two coordinates (Haversine formula)
    const calculateDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371; // Earth's radius in kilometers
        const dLat = (lat2 - lat1) * Math.PI / 180;
        const dLon = (lon2 - lon1) * Math.PI / 180;
        const a = 
            Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
            Math.sin(dLon/2) * Math.sin(dLon/2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
        return Math.round((R * c) * 10) / 10; // Round to 1 decimal place
    };

    // Filter items based on active category
    const getFilteredItems = () => {
        const allItems = [...attractions, ...events];
        if (activeCategory === 'all') {
            return allItems.sort((a, b) => a.distance - b.distance);
        }
        return allItems
            .filter(item => item.category === activeCategory)
            .sort((a, b) => a.distance - b.distance);
    };

    // Format date for events
    const formatEventDate = (date) => {
        const today = new Date();
        const diffTime = date - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Tomorrow';
        if (diffDays < 7) return `In ${diffDays} days`;
        return date.toLocaleDateString();
    };

    // Render price level
    const renderPriceLevel = (level) => {
        if (level === 0) return <CBadge color="success">Free</CBadge>;
        const dollars = '$'.repeat(level);
        return <CBadge color="info">{dollars}</CBadge>;
    };

    // Render rating stars
    const renderRating = (rating) => {
        const stars = '★'.repeat(Math.floor(rating)) + '☆'.repeat(5 - Math.floor(rating));
        return <span className="rating">{stars} {rating}</span>;
    };

    if (!coordinates) {
        return (
            <CCard className="suggestions-card">
                <CCardBody className="text-center">
                    <div className="no-location">
                        <div className="no-location-icon">📍</div>
                        <h5>Location Access Needed</h5>
                        <p>Enable location access to discover amazing places and events near you!</p>
                    </div>
                </CCardBody>
            </CCard>
        );
    }

    return (
        <div className="local-suggestions">
            <div className="suggestions-header">
                <h4>🌟 Discover {locationName}</h4>
                <p className="location-subtitle">
                    {getFilteredItems().length} places and events near you
                </p>
            </div>

            {/* Category Filter */}
            <div className="category-filter">
                <CButtonGroup size="sm" className="w-100">
                    {categories.map(category => (
                        <CButton
                            key={category.id}
                            color={activeCategory === category.id ? 'primary' : 'outline-primary'}
                            onClick={() => setActiveCategory(category.id)}
                            className="category-btn"
                        >
                            <span className="category-icon">{category.icon}</span>
                            <span className="category-name">{category.name}</span>
                        </CButton>
                    ))}
                </CButtonGroup>
            </div>

            {error && <CAlert color="danger" className="mt-2">{error}</CAlert>}

            {isLoading ? (
                <div className="loading-section">
                    <CSpinner size="sm" className="me-2" />
                    <span>Finding amazing places near you...</span>
                </div>
            ) : (
                <div className="suggestions-list">
                    {getFilteredItems().slice(0, 8).map(item => (
                        <div key={item.id} className="suggestion-item">
                            <div className="item-image">
                                {item.image ? (
                                    <img src={item.image} alt={item.name} />
                                ) : (
                                    <div className="placeholder-image">
                                        {categories.find(c => c.id === item.category)?.icon || '📍'}
                                    </div>
                                )}
                            </div>
                            
                            <div className="item-content">
                                <div className="item-header">
                                    <h6 className="item-name">{item.name}</h6>
                                    <span className="item-distance">{item.distance}km</span>
                                </div>
                                
                                <div className="item-type">
                                    <CBadge color="secondary" className="type-badge">
                                        {item.type}
                                    </CBadge>
                                    {item.rating && renderRating(item.rating)}
                                    {item.priceLevel !== undefined && renderPriceLevel(item.priceLevel)}
                                </div>
                                
                                <p className="item-description">{item.description}</p>
                                
                                {item.date && (
                                    <div className="event-details">
                                        <div className="event-date">
                                            📅 {formatEventDate(item.date)}
                                        </div>
                                        <div className="event-time">
                                            🕐 {item.time}
                                        </div>
                                        {item.price && (
                                            <div className="event-price">
                                                💰 {item.price}
                                            </div>
                                        )}
                                    </div>
                                )}
                                
                                <div className="item-address">
                                    📍 {item.address}
                                </div>
                            </div>
                        </div>
                    ))}
                    
                    {getFilteredItems().length === 0 && (
                        <div className="no-results">
                            <div className="no-results-icon">🔍</div>
                            <p>No {activeCategory === 'all' ? 'places' : categories.find(c => c.id === activeCategory)?.name.toLowerCase()} found in your area.</p>
                            <CButton 
                                color="primary" 
                                size="sm" 
                                onClick={() => setActiveCategory('all')}
                            >
                                Show All Categories
                            </CButton>
                        </div>
                    )}
                </div>
            )}

            <div className="suggestions-footer">
                <CButton 
                    color="outline-primary" 
                    size="sm" 
                    onClick={fetchLocalData}
                    disabled={isLoading}
                >
                    🔄 Refresh
                </CButton>
            </div>
        </div>
    );
};

export default Suggestions;